import { ref, computed, watch } from 'vue';
import { createGlobalState } from '@vueuse/core';
import { getFamilyPatentInfoApi } from 'library-@/apis/core_basic_api/family';
import { useFamilySearchPage } from './use-family-search-page';
import { useFamilySearchUrlParams } from './use-family-search-url-params';
import { create } from '../utils/deferred';
import type { IDeferred } from '../utils/deferred';

interface IPatentInfo {
    patsnap_family_count: number;
    patent_id: string;
    title: string;
    translation: boolean;
}

export const useFamilySearchPatentInfo = createGlobalState(() => {
    const { currentActiveItem } = useFamilySearchPage();
    const { q } = useFamilySearchUrlParams();

    const deferred = ref<IDeferred>(create<Partial<IPatentInfo>>());
    const patentInfo = ref<Partial<IPatentInfo>>({});
    const isLoading = ref(false);

    const currentActivePatentId = computed(() => currentActiveItem.value.patent_id);

    const getPatentInfo = async (patentId: string, q: string) => {
        isLoading.value = true;
        if (deferred.value.completed) deferred.value = create();
        try {
            const res = await getFamilyPatentInfoApi({
                patentId,
                q,
            }) as IPatentInfo;
            if (res.patent_id === currentActivePatentId.value) {
                patentInfo.value = res;
                isLoading.value = false;
                deferred.value.resolve(res);
            }
        } catch (error) {
            deferred.value.resolve({});
            isLoading.value = false;
        }
    };

    watch(currentActivePatentId, async (val) => {
        if (!val) return;
        await getPatentInfo(val, q.value);
    }, { immediate: true });

    return {
        isLoading,
        patentInfo,
        deferred,
    };
});
